import React, { useState, useEffect } from 'react';
import styled from "styled-components";
import { useParams, useHistory, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getSeriesInfo, getVodInfo } from "../../other/load-playlist";
import { optimizeName } from "../../other/vod-series-name-optimizer";
import Seasons from "../Series/Seasons";
import { getImageUrl } from "../../other/axios";
import DB from "../../other/local-db";
import { setPlaylist } from "../../actions/set-Playlist";
import { generateUrl } from "../../other/generate-url";

const Container = styled.div`
    position: fixed;
    top: 0; left: 0; width: 100vw; height: 100vh;
    background-color: rgba(0,0,0,0.85);
    backdrop-filter: blur(10px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
`;

const Box = styled.div`
    width: 90vw;
    max-width: 1100px;
    height: 90vh;
    background-color: #141414;
    border-radius: 1rem;
    box-shadow: 0 0 50px rgba(0,0,0,0.5);
    overflow-y: auto;
    position: relative;
    border: 1px solid rgba(255,255,255,0.1);
    @media (max-width: 768px) {
        width: 95vw;
        height: 95vh;
        border-radius: 0.5rem;
    }
`;

const Header = styled.div`
    height: 45vh;
    background-color: #000;
    position: relative;
    overflow: hidden;
    @media (max-width: 768px) {
        height: 30vh;
    }
`;

const ImgHovered = styled.div`
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    filter: brightness(0.6);
`;

const CloseBtn = styled.button`
    position: absolute;
    top: 20px;
    right: 20px;
    width: 45px;
    height: 45px;
    background: rgba(0,0,0,0.6);
    border: none;
    border-radius: 50%;
    color: white;
    font-size: 20px;
    cursor: pointer;
    z-index: 100;
    transition: all 0.2s;
    &:hover { background: #e50914; transform: scale(1.1); }
`;

const Body = styled.div`
    padding: 2rem 3rem;
    color: white;
    @media (max-width: 768px) {
        padding: 1.5rem 1.5rem;
    }
`;

const InfoRow = styled.div`
    display: flex;
    gap: 30px;
    margin-top: -80px;
    position: relative;
    z-index: 5;
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
        margin-top: -120px;
        gap: 20px;
    }
`;

const Poster = styled.img`
    width: 220px;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.8);
    border: 1px solid rgba(255,255,255,0.1);
    @media (max-width: 768px) {
        width: 160px;
    }
`;

const Details = styled.div`
    flex: 1;
    padding-top: 90px;
    @media (max-width: 768px) {
        padding-top: 0;
        text-align: center;
    }
`;

const Title = styled.h1`
    font-size: 2.5rem;
    font-weight: 900;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    @media (max-width: 768px) {
        font-size: 1.6rem;
    }
`;

const Meta = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    color: #a3a3a3;
    @media (max-width: 768px) {
        justify-content: center;
        flex-wrap: wrap;
    }
`;

const Rating = styled.span`
    color: #46d369;
    font-weight: bold;
`;

const Description = styled.p`
    font-size: 1.1rem;
    line-height: 1.6;
    color: #d2d2d2;
    margin-bottom: 2rem;
    @media (max-width: 768px) {
        font-size: 0.95rem;
    }
`;

const ButtonGroup = styled.div`
    display: flex; 
    gap: 15px; 
    margin-bottom: 3rem;
    flex-wrap: wrap;
    @media (max-width: 768px) {
        flex-direction: column;
        width: 100%;
    }
`;

const PlayButton = styled.button`
    background: white;
    color: black;
    border: none;
    padding: 0.8rem 2.5rem;
    border-radius: 4px;
    font-weight: bold;
    font-size: 1.1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: all 0.2s;
    &:hover { background: #e6e6e6; transform: scale(1.05); }
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const FavoriteButton = styled.button`
    background: rgba(109, 109, 110, 0.7);
    color: white;
    border: none;
    padding: 0.8rem 1.5rem;
    border-radius: 4px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    &:hover { background: rgba(109, 109, 110, 0.9); }
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const DownloadButton = styled.button`
    background: #00d2ff;
    color: black;
    border: none;
    padding: 0.8rem 1.5rem;
    border-radius: 4px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: all 0.2s;
    &:hover { background: #00b8e6; transform: scale(1.05); }
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const MoreInfo = () => {
    const { category, stream_id, playingMode } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();

    const [info, setInfo] = useState(null);
    const [seasons, setSeasons] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFavorite, setIsFavorite] = useState(false);

    const playlist = useSelector(state => state.playlist) || [];
    const stream = playlist.find(x => (playingMode === "series" ? x.series_id : x.stream_id)?.toString() === stream_id?.toString());

    useEffect(() => {
        if (!stream) {
            history.replace(`/${playingMode}/`);
            return;
        }

        setIsFavorite(!!DB.findOne(playingMode, stream_id, true));

        const loadData = async () => {
            setIsLoading(true);
            try {
                const result = (playingMode === "series")
                    ? await getSeriesInfo(stream.series_id, stream.name, false, stream.tmdb)
                    : await getVodInfo(stream.stream_id, stream.name, stream.tmdb);

                if (result) {
                    setInfo(result.info || result);
                    if (playingMode === "series") {
                        setSeasons(result.episodes);
                    }
                }
            } catch (err) {
                console.error("Error loading info:", err);
            }
            setIsLoading(false);
        };
        loadData();
    }, [stream_id, playingMode, stream, history]);

    const handlePlay = () => {
        if (!info) return;
        const itemData = { ...(info.info || info) };
        const finalId = stream_id;
        
        itemData.id = finalId;
        if (playingMode === "series") itemData.series_id = finalId;
        else itemData.stream_id = finalId;

        dispatch(setPlaylist([itemData]));
        history.push(`/${playingMode}/category/${category}/${stream_id}/play/`);
    };

    const handleDownload = () => {
        const url = generateUrl(playingMode === "series" ? "series" : "movie", stream_id, stream?.container_extension || "mp4");
        window.open(url, '_blank');
    };

    const toggleFavorite = (e) => {
        e.stopPropagation();
        if (isFavorite) {
            DB.del(playingMode, stream_id, true);
        } else {
            DB.set(playingMode, stream_id, { id: stream_id }, true);
        }
        setIsFavorite(!isFavorite);
    };

    if (isLoading) return (
        <Container>
            <div style={{ color: '#00d2ff', fontSize: '1.5rem', fontWeight: 'bold' }}>Carregando...</div>
        </Container>
    );

    const cover = info?.cover || info?.cover_big || info?.movie_image || stream?.stream_icon;
    const backdrop = info?.backdrop_path?.[0] || cover;
    const name = info?.name || stream?.name;
    const plot = info?.description || info?.plot || "Sem sinopse disponível.";

    return (
        <Container onClick={() => history.goBack()}>
            <Box onClick={e => e.stopPropagation()}>
                <CloseBtn onClick={() => history.goBack()}>
                    <i className="fas fa-times"></i>
                </CloseBtn>
                
                <Header>
                    <ImgHovered style={{ backgroundImage: `url(${getImageUrl(backdrop)})` }} />
                </Header>

                <Body>
                    <InfoRow>
                        <Poster src={getImageUrl(cover)} alt={name} />
                        <Details>
                            <Title>{name}</Title>
                            <Meta>
                                {info?.rating && <Rating>{info.rating} / 10</Rating>}
                                {info?.releasedate && <span>{info.releasedate}</span>}
                                {info?.genre && <span>{info.genre}</span>}
                            </Meta>
                            <Description>{plot}</Description>
                            
                            <ButtonGroup>
                                {playingMode !== "series" && (
                                    <PlayButton onClick={handlePlay}>
                                        <i className="fas fa-play"></i> Assistir Agora
                                    </PlayButton>
                                )}
                                <FavoriteButton onClick={toggleFavorite}>
                                    <i className={`${isFavorite ? 'fas' : 'far'} fa-star`}></i>
                                    {isFavorite ? ' Remover' : ' Favorito'}
                                </FavoriteButton>
                                <DownloadButton onClick={handleDownload}>
                                    <i className="fas fa-download"></i> Download
                                </DownloadButton>
                            </ButtonGroup>
                        </Details>
                    </InfoRow>

                    {playingMode === "series" && seasons && (
                        <div style={{ marginTop: '2rem', borderTop: '1px solid #333', paddingTop: '2rem' }}>
                            <Seasons seasonData={seasons} cover={cover} />
                        </div>
                    )}
                </Body>
            </Box>
        </Container>
    );
};

export default MoreInfo;
